import { NetworkArray, NetworkPaginationSlice, SortableEntity } from 'typed/api/entities';
import { IReducer } from './';
import { IEntitySet, isNetworkArray } from './entityReducers';
import { DeepPartial, deepAssign } from 'typed/utils';

export default function nestableEntitiesReducer<E extends SortableEntity, R extends IReducer<NetworkPaginationSlice<E>, any>>(def: NetworkPaginationSlice<E>, prop: keyof E | null, reducer: R): R;
export default function nestableEntitiesReducer<E extends SortableEntity, R extends IReducer<NetworkArray<E>, any>>(def: NetworkArray<E>, prop: keyof E | null, reducer: R): R;
export default function nestableEntitiesReducer<E extends SortableEntity, R extends IReducer<E[], any>>(def: E[], prop: keyof E | null, reducer: R): R;
export default function nestableEntitiesReducer<E extends SortableEntity, R extends IReducer<IEntitySet<E>, any>>(def: IEntitySet<E>, prop: keyof E | null, reducer: R): R {
    return (function sortableEntitiesReducer(state: any = def, action: any): any {
        const
            oldState = state,
            newState = reducer(state, action);

        if (!oldState || newState === oldState) {
            return newState;
        }

        const oldData = isNetworkArray(oldState) ? oldState.Data : oldState;
        let newData = isNetworkArray(newState) ? newState.Data : newState;

        newData = deepAssign(newData, newData
            .groupBy(entity => prop && Object.prototype.hasOwnProperty.call(entity, prop) ? (entity as unknown as any)[prop] : false)
            .sortBy(group => group.key)
            .map((group) => {
                const
                    set = group
                        .filter(entity => entity.Sort && oldData.getByID(entity.ID) !== newData.getByID(entity.ID))
                        .sortBy(entity => entity.Sort),
                    unset = group
                        .filter(entity => !entity.Sort || oldData.getByID(entity.ID) === newData.getByID(entity.ID))
                        .sortBy(entity => entity.Sort || group.length - set.length + Math.abs(entity.ID));

                let autoOffset = 0;

                return [...unset, ...set]
                    .map((_, index) => set.find(entity => entity.Sort === index + 1) || unset[autoOffset++])
                    .concat([...set.filter(entity => entity.Sort > unset.length + set.length)])
                    .filter(v => v)
                    .map((entity, index) => deepAssign(entity as unknown as any, { Sort: index + 1 })) as DeepPartial<E>[];
            })
            .flatten()
        );

        return isNetworkArray(newState) ? deepAssign(newState, { Data: newData }) : newData;
    }) as any;
}
